import React from 'react';
import { 
  Users, 
  AlertTriangle, 
  AlertCircle,
  DollarSign,
  ArrowRight
} from 'lucide-react';

const farmColors = [
  '#E91E8C', '#9C27B0', '#3F51B5', '#00BCD4', '#4CAF50',
  '#FF9800', '#F44336', '#673AB7', '#2196F3', '#FFEB3B'
];

const FarmsList = ({ farms, onFarmSelect, selectedFarm }) => {
  
  const getRiskBadge = (riskLevel) => {
    const base = "px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1";
    switch (riskLevel) {
      case 'high':
        return <span className={`${base} bg-red-500/20 text-red-400`}><AlertTriangle className="h-3 w-3" />Высокий</span>;
      case 'medium':
        return <span className={`${base} bg-yellow-500/20 text-yellow-400`}><AlertCircle className="h-3 w-3" />Средний</span>;
      default:
        return <span className={`${base} bg-green-500/20 text-green-400`}>Низкий</span>;
    }
  };
  
  const getConnectionLabel = (type) => {
    switch (type) {
      case 'token_transfer': return 'Переводы токенов';
      case 'time_pattern': return 'Одновременные покупки';
      case 'same_amount': return 'Одинаковые суммы';
      default: return type;
    }
  };
  
  const formatBalance = (balance) => {
    if (balance >= 1e9) return `${(balance / 1e9).toFixed(2)}B`;
    if (balance >= 1e6) return `${(balance / 1e6).toFixed(2)}M`;
    if (balance >= 1e3) return `${(balance / 1e3).toFixed(1)}K`;
    return balance.toFixed(0);
  };
  
  if (!farms || farms.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-3">🔍</div>
        <p className="text-gray-400 text-sm">Кластеры не обнаружены</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {farms.map((farm) => {
        const farmColor = farmColors[(farm.id - 1) % farmColors.length];
        const isSelected = selectedFarm === farm.id;
        
        return (
          <div 
            key={farm.id}
            className={`rounded-xl cursor-pointer transition-all ${isSelected ? 'ring-1 ring-white/30' : ''}`}
            style={{
              background: `linear-gradient(135deg, ${farmColor}15 0%, ${farmColor}05 100%)`,
              borderLeft: `3px solid ${farmColor}`
            }}
            onClick={() => onFarmSelect(farm.id === selectedFarm ? null : farm.id)}
          >
            {/* Header */}
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: farmColor }}
                  >
                    {farm.wallet_count}
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">Кластер #{farm.id}</div>
                    <div className="text-gray-500 text-[10px]">{farm.wallet_count} кошельков</div>
                  </div>
                </div>
                {getRiskBadge(farm.risk_level)}
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="bg-black/20 rounded-lg p-2">
                  <div className="text-[10px] text-gray-500">Общий баланс</div>
                  <div className="text-white font-bold text-sm">{formatBalance(farm.total_balance)}</div>
                </div>
                <div className="bg-black/20 rounded-lg p-2">
                  <div className="text-[10px] text-gray-500">Стоимость</div>
                  <div className="text-green-400 font-bold text-sm">${(farm.total_balance_usd || 0).toFixed(2)}</div>
                </div>
              </div>
              
              {/* Connection type */}
              <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-black/20 rounded-lg px-2 py-1.5">
                <DollarSign className="h-3 w-3" />
                {getConnectionLabel(farm.main_connection_type)}
              </div>
            </div>
            
            {/* Wallets - compact */}
            <div className="px-3 pb-3">
              <div className="text-[10px] text-gray-500 mb-1.5">Кошельки:</div>
              <div className="flex flex-wrap gap-1">
                {farm.wallets.slice(0, 5).map((wallet) => (
                  <div 
                    key={wallet.address}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/30 text-[10px] hover:bg-black/50 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://tonviewer.com/${wallet.address}`, '_blank');
                    }}
                    title="Открыть в TonViewer"
                  >
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: farmColor }} />
                    <span className="text-gray-400 font-mono hover:text-white">
                      {wallet.address.slice(0, 4)}...{wallet.address.slice(-3)}
                    </span>
                    <span className="text-gray-600">{formatBalance(wallet.balance)}</span>
                  </div>
                ))}
                {farm.wallets.length > 5 && (
                  <div className="px-1.5 py-0.5 rounded bg-black/30 text-[10px] text-gray-500">
                    +{farm.wallets.length - 5} ещё
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FarmsList;
